/**
 * A Chart in a React frame. The canvas painter itself is untouched - it
 * doubles as a 3D HMI texture elsewhere and has no business being React -
 * so this wraps one instance per mount, repaints when the options object
 * changes identity, and disposes the resize observer on the way out.
 *
 * Callers memoize `options` on the data it plots; an 8 Hz store tick whose
 * numbers did not move then skips the repaint entirely.
 */

import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { Chart, type ChartOptions } from '../Chart'

/** How far past its designed height an unlocked chart may scale with its column. */
const GROW = 1.5

export function ChartView({
  width = 360,
  height = 190,
  options,
  frameStyle,
  lockHeight = false,
}: {
  width?: number
  height?: number
  options: ChartOptions
  frameStyle?: CSSProperties
  /**
   * Pin the DISPLAYED height to `height` px while the width keeps following
   * the container. By default the canvas scales at its logical aspect ratio,
   * so a chart in a fluid column renders at whatever height its width
   * implies - which is right for a chart alone in a panel, and wrong for one
   * that has promised to stand exactly as tall as a viewer beside it. The
   * Chart's own resize path re-syncs the backing store and re-derives its
   * logical extent from the real aspect, so nothing distorts: the drawing
   * just gains or gives back vertical room.
   */
  lockHeight?: boolean
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    const chart = new Chart(width, height)
    chartRef.current = chart
    if (lockHeight) chart.canvas.style.height = `${height}px`
    // Unlocked, the canvas scales at its logical aspect ratio - and on a
    // 1200 px panel a chart drawn for 430 x 195 stood 740 px tall, one chart
    // to a screen, its controls a scroll away. It may still grow with its
    // column, but only to GROW times the height it was designed at; past
    // that it gains width and the Chart re-derives its extent, exactly as it
    // does under lockHeight.
    else chart.canvas.style.maxHeight = `${Math.round(height * GROW)}px`
    hostRef.current!.appendChild(chart.canvas)
    return () => {
      chart.dispose()
      chart.canvas.remove()
      chartRef.current = null
    }
  }, [width, height])

  useEffect(() => {
    chartRef.current?.render(options)
  }, [options, width, height])

  return <div ref={hostRef} className="chart-wrap" style={frameStyle} />
}
